// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { EVENT_NAMES } from '../../utils/eventLogging/eventNames';

import Select from '../ui/Select';
import { useTranslation } from '../../i18n/i18n';

export default function SupplierFilter({ occurringSuppliers, setSupplierFilter }) {
  const { logEventCustom, suppliers, currentUser } = React.useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const [selectedSupplier, setSelectedSupplier] = useState('');

  function handleSupplierChange(supplier_id) {
    setSelectedSupplier(supplier_id);
    setSupplierFilter(supplier_id);

    // Log the event to Firebase Analytics
    if (logEventCustom) {
      logEventCustom(EVENT_NAMES.FILTER, { type: 'supplier', value: supplier_id });
    }
  }

  // Sort suppliers alphabetically by their display names
  const sortedSuppliers = React.useMemo(() => {
    return [...occurringSuppliers]
      .map((key) => {
        const supplier = suppliers?.[key];
        let displayValue;
        if (supplier?.supplier_code) {
          displayValue = `${supplier?.supplier_code} ${supplier?.display_name}`;
        } else {
          displayValue = supplier?.display_name ?? '';
        }
        return [key, displayValue];
      })
      .sort(([, valueA], [, valueB]) => {
        return valueA.localeCompare(valueB, undefined, { numeric: true });
      });
  }, [occurringSuppliers, suppliers]);

  const options = React.useMemo(() => {
    return [
      { value: '', label: t('supplier_filter_all') },
      ...sortedSuppliers
        .filter(([supplier_id]) => supplier_id !== undefined && supplier_id !== '')
        .map(([supplier_id, displayValue]) => ({ value: supplier_id, label: displayValue })),
    ];
  }, [sortedSuppliers]);

  return (
    <Select
      className='w-[24rem] min-w-[24rem] max-w-[24rem] shrink-0'
      triggerLabelClassName='font-medium'
      prefix={<span className='text-[#8a8c95] dark:text-[#90a1b9]'>{t('supplier_filter_prefix')}</span>}
      value={selectedSupplier ?? ''}
      options={options}
      onChange={handleSupplierChange}
    />
  );
}
